import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { InfoOutlined as InfoOutlinedIcon, SmartToy as BotIcon } from '@mui/icons-material';
import { INDIGO, SLATE } from '../../constants/Colors';
import SanitizedHtml from '../../components/SanitizedHtml';

function MediaImage({ mediaObj }) {
  const [url, setUrl] = useState(null);

  useEffect(() => {
    mediaObj.getContentTemporaryUrl().then(setUrl).catch(() => {});
  }, [mediaObj]);

  if (!url) return null;
  return (
    <Box
      component="img"
      src={url}
      alt={mediaObj.filename || "attachment"}
      sx={{
        maxWidth: 280,
        maxHeight: 280,
        objectFit: "contain",
        borderRadius: "8px",
        border: `1px solid ${SLATE[200]}`,
        display: "block",
      }}
    />
  );
}

function BotAvatar() {
  return (
    <Box
      sx={{
        width: 36,
        height: 36,
        borderRadius: '50%',
        background: `linear-gradient(135deg, ${INDIGO[500]} 0%, #7c3aed 100%)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        alignSelf: 'flex-start',
        mt: '2px',
      }}
    >
      <BotIcon sx={{ fontSize: '1.1rem', color: '#fff' }} />
    </Box>
  );
}

function ChangesIcon({ changes }) {
  const tooltipContent = (
    <Box sx={{ py: '2px' }}>
      <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#fff', mb: '4px' }}>
        Files updated:
      </Typography>
      {changes.map((c, i) => (
        <Typography key={i} sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.88)', lineHeight: 1.6 }}>
          • {c}
        </Typography>
      ))}
    </Box>
  );

  return (
    <Tooltip
      title={tooltipContent}
      enterDelay={200}
      enterNextDelay={200}
      placement="top-end"
      arrow
    >
      <InfoOutlinedIcon
        sx={{
          fontSize: '0.875rem',
          color: SLATE[400],
          flexShrink: 0,
          verticalAlign: 'middle',
          cursor: 'default',
          '&:hover': { color: SLATE[600] },
          transition: 'color 0.15s',
        }}
      />
    </Tooltip>
  );
}

function BotMessage({ content, authorName, media = [], changes }) {
  return (
    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
      <BotAvatar />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, maxWidth: 680 }}>
        {authorName && (
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: SLATE[500] }}>
            {authorName}
          </Typography>
        )}
        <Box
          sx={{
            position: 'relative',
            bgcolor: '#fff',
            border: `1px solid ${SLATE[200]}`,
            borderRadius: '12px',
            px: 2.5,
            py: 2,
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
          }}
        >
          {changes?.length > 0 && (
            <Box sx={{ position: 'absolute', top: '8px', right: '10px', lineHeight: 0 }}>
              <ChangesIcon changes={changes} />
            </Box>
          )}
          {media.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: content ? 1.5 : 0 }}>
              {media.map((m) => <MediaImage key={m.sid} mediaObj={m} />)}
            </Box>
          )}
          {content && <SanitizedHtml content={content} sx={{ color: SLATE[800] }} />}
        </Box>
      </Box>
    </Box>
  );
}

function UserMessage({ content, authorName, media = [] }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
      {authorName && (
        <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: SLATE[500] }}>
          {authorName}
        </Typography>
      )}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1, maxWidth: 560 }}>
        {media.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'flex-end' }}>
            {media.map((m) => <MediaImage key={m.sid} mediaObj={m} />)}
          </Box>
        )}
        {content && (
          <Box
            sx={{
              bgcolor: INDIGO[600],
              borderRadius: '12px',
              px: 2.5,
              py: 1.5,
              width: '100%',
            }}
          >
            <Typography
              sx={{
                fontSize: '0.875rem',
                lineHeight: 1.6,
                color: '#fff',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {content}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}

const LOAD_MORE_SCROLL_THRESHOLD = 80;

export default function ChatMessages({
  messages,
  participants = {},
  hasMore = false,
  isLoadingMore = false,
  onLoadMore,
}) {
  const containerRef = useRef(null);
  const prevScrollHeightRef = useRef(0);
  const prevMessageCountRef = useRef(0);
  const isPrependingRef = useRef(false);

  const handleScroll = useCallback(() => {
    const el = containerRef.current;
    if (!el || !hasMore || isLoadingMore) return;

    if (el.scrollTop < LOAD_MORE_SCROLL_THRESHOLD) {
      isPrependingRef.current = true;
      prevScrollHeightRef.current = el.scrollHeight;
      if (onLoadMore) onLoadMore();
    }
  }, [hasMore, isLoadingMore, onLoadMore]);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    if (isPrependingRef.current) {
      // Older messages were inserted above the fold — keep the same messages in view
      // instead of letting the new content push the scroll position down.
      el.scrollTop += el.scrollHeight - prevScrollHeightRef.current;
      isPrependingRef.current = false;
    } else if (messages.length !== prevMessageCountRef.current) {
      el.scrollTop = el.scrollHeight;
    }
    prevMessageCountRef.current = messages.length;
  }, [messages]);

  return (
    <Box
      ref={containerRef}
      onScroll={handleScroll}
      sx={{
        flex: 1,
        overflowY: 'auto',
        overflowAnchor: 'none',
        px: 4,
        py: 3,
        display: 'flex',
        flexDirection: 'column',
        gap: 2.5,
      }}
    >
      {isLoadingMore && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
          <Typography sx={{ fontSize: '0.75rem', color: SLATE[500] }}>
            Loading earlier messages…
          </Typography>
        </Box>
      )}
      {messages.map(msg => {
        const participantName = participants[msg.author];
        if (!participantName) {
          return <BotMessage key={msg.id} content={msg.content} authorName="Agent" media={msg.media} changes={msg.changes} />;
        }
        return <UserMessage key={msg.id} content={msg.content} authorName={participantName} media={msg.media} />;
      })}
      <div />
    </Box>
  );
}
